/**
 * Art of Revival - Folder Management System
 * Manages designer folders for organizing products
 * Works alongside the existing favorites (hearts) system
 */

const AORFolders = {
  STORAGE_KEY: 'aor_folders',
  
  // Initialize folder system
  init() {
    const folders = this.getAllFolders();
    if (!folders || typeof folders !== 'object') {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({}));
    }
    this.updateFolderCounts();
  },

  // Get all folders
  getAllFolders() {
    const data = localStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  },

  // Create new folder
  createFolder(name) {
    if (!name || name.trim() === '') return null;
    
    const folders = this.getAllFolders();
    const folderId = this.generateId();
    
    folders[folderId] = {
      id: folderId,
      name: name.trim(),
      items: [],
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    };
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(folders));
    this.updateFolderCounts();
    return folderId;
  },

  // Rename folder
  renameFolder(folderId, newName) {
    if (!newName || newName.trim() === '') return false;
    
    const folders = this.getAllFolders();
    if (!folders[folderId]) return false;
    
    folders[folderId].name = newName.trim();
    folders[folderId].updated = new Date().toISOString();
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(folders));
    return true;
  },

  // Delete folder
  deleteFolder(folderId) {
    const folders = this.getAllFolders();
    if (!folders[folderId]) return false;
    
    delete folders[folderId];
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(folders));
    this.updateFolderCounts();
    return true;
  },

  // Get single folder
  getFolder(folderId) {
    const folders = this.getAllFolders();
    return folders[folderId] || null;
  },

  // Add item to folder
  addItemToFolder(folderId, productData) {
    const folders = this.getAllFolders();
    if (!folders[folderId]) return false;
    
    // Check if item already exists
    const existingIndex = folders[folderId].items.findIndex(
      item => item.id === productData.id
    );
    
    if (existingIndex === -1) {
      folders[folderId].items.push({
        id: productData.id,
        title: productData.title,
        handle: productData.handle,
        price: productData.price,
        image: productData.image,
        added: new Date().toISOString()
      });
      
      folders[folderId].updated = new Date().toISOString();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(folders));
      this.updateFolderCounts();
      return true;
    }
    
    return false; // Already exists
  },

  // Remove item from folder
  removeItemFromFolder(folderId, productId) {
    const folders = this.getAllFolders();
    if (!folders[folderId]) return false;
    
    folders[folderId].items = folders[folderId].items.filter(
      item => item.id !== productId
    );
    
    folders[folderId].updated = new Date().toISOString();
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(folders));
    this.updateFolderCounts();
    return true;
  },

  // Check if product is in any folder
  isInAnyFolder(productId) {
    const folders = this.getAllFolders();
    return Object.values(folders).some(folder => 
      folder.items.some(item => item.id === productId)
    );
  },

  // Get folders containing a product
  getFoldersForProduct(productId) {
    const folders = this.getAllFolders();
    return Object.values(folders).filter(folder =>
      folder.items.some(item => item.id === productId)
    );
  },

  // Update folder count badges in UI
  updateFolderCounts() {
    const folders = this.getAllFolders();
    const totalFolders = Object.keys(folders).length;
    const totalItems = Object.values(folders).reduce(
      (sum, folder) => sum + folder.items.length, 0
    );
    
    // Update header badge if it exists
    const badge = document.querySelector('[data-folder-count]');
    if (badge) {
      badge.textContent = totalFolders;
      badge.style.display = totalFolders > 0 ? 'inline-block' : 'none';
    }
    
    // Dispatch event for other components to listen
    document.dispatchEvent(new CustomEvent('aor:folders-updated', {
      detail: { totalFolders, totalItems }
    }));
  },

  // Generate unique ID
  generateId() {
    return 'f_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  },

  // Export folder data for email/sharing
  exportFolder(folderId) {
    const folder = this.getFolder(folderId);
    if (!folder) return null;
    
    return {
      id: folder.id,
      name: folder.name,
      itemCount: folder.items.length,
      items: folder.items,
      created: folder.created,
      shareUrl: `${window.location.origin}/pages/folder-view?id=${folderId}`
    };
  },

  // Generate email mailto link
  generateEmailLink(folderId) {
    const folderData = this.exportFolder(folderId);
    if (!folderData) return null;
    
    const subject = encodeURIComponent(`${folderData.name} - Art of Revival Collection`);
    
    let body = `I'd like to share this curated collection from Art of Revival:%0D%0A%0D%0A`;
    body += `${folderData.name}%0D%0A`;
    body += `${folderData.itemCount} ${folderData.itemCount === 1 ? 'piece' : 'pieces'}%0D%0A%0D%0A`;
    body += `View the full collection:%0D%0A`;
    body += `${folderData.shareUrl}%0D%0A%0D%0A`;
    body += `----%0D%0A`;
    
    folderData.items.forEach((item, index) => {
      body += `${index + 1}. ${item.title}%0D%0A`;
      body += `   ${window.location.origin}/products/${item.handle}%0D%0A%0D%0A`;
    });
    
    return `mailto:?subject=${subject}&body=${body}`;
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => AORFolders.init());
} else {
  AORFolders.init();
}